import React from 'react';
import Icon from '../../../components/AppIcon';

const PrivacyPolicyContent = ({ searchQuery }) => {
  // Helper function to highlight search terms
  const highlightSearchTerm = (text, searchQuery) => {
    if (!searchQuery?.trim()) return text;
    
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text?.split(regex);
    
    return parts?.map((part, index) => 
      regex?.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const privacyContent = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: 'FileText',
      content: `We collect information to provide and improve our services. The types of information we collect include:

      Personal Information:
      • Name, email address, and contact details
      • Professional information (job title, company, work history)
      • Account credentials and preferences
      • Payment and billing information

      Candidate Information:
      • Resumes, cover letters, and application materials
      • Interview notes and feedback
      • Communication history and scheduling data
      • Assessment results and evaluations

      Technical Information:
      • IP address, browser type, and device information
      • Usage patterns and feature interactions
      • Log files and system performance data
      • Cookies and similar tracking technologies

      We collect this information when you provide it directly, through your use of our services, or from third-party integrations.`
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: 'Settings',
      content: `We use the information we collect for legitimate business purposes, including:

      Service Provision:
      • Operating and maintaining our platform
      • Processing job applications and managing candidates
      • Facilitating communication between users
      • Providing customer support and technical assistance

      Service Improvement:
      • Analyzing usage patterns to enhance features
      • Developing new functionality and services
      • Conducting research and data analysis
      • Personalizing user experiences

      Business Operations:
      • Processing payments and managing subscriptions
      • Sending service-related communications
      • Complying with legal obligations
      • Protecting against fraud and security threats

      We do not sell your personal information to third parties for marketing purposes.`
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing and Disclosure',
      icon: 'Share2',
      content: `We may share your information in the following circumstances:

      With Your Consent:
      • When you explicitly authorize sharing
      • For integrations you enable (job boards, HR tools)
      • When participating in collaborative hiring processes

      Service Providers:
      • Cloud hosting and infrastructure providers
      • Payment processing companies
      • Email and communication service providers
      • Analytics and monitoring tools

      Legal Requirements:
      • To comply with applicable laws and regulations
      • In response to valid legal process
      • To protect our rights and prevent legal liability
      • In connection with business transfers or sales

      We implement contractual protections to ensure service providers handle your data appropriately and only for authorized purposes.`
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: 'Shield',
      content: `We implement comprehensive security measures to protect your information:

      Technical Safeguards:
      • Industry-standard encryption in transit and at rest
      • Multi-factor authentication and access controls
      • Regular security monitoring and threat detection
      • Secure coding practices and vulnerability testing

      Administrative Safeguards:
      • Employee training on data protection
      • Background checks for personnel with data access
      • Incident response and breach notification procedures
      • Regular security policy reviews and updates

      Physical Safeguards:
      • Secure data centers with restricted access
      • Environmental controls and monitoring
      • Backup and disaster recovery systems
      • Proper disposal of hardware and storage media

      While we implement strong security measures, no system is 100% secure. We encourage users to practice good security hygiene with their accounts.`
    },
    {
      id: 'user-rights',
      title: 'Your Rights and Choices',
      icon: 'User',
      content: `You have several rights regarding your personal information:

      Access and Portability:
      • View and download your personal data
      • Receive data in a structured, machine-readable format
      • Transfer data to another service provider

      Correction and Updates:
      • Update inaccurate or incomplete information
      • Modify your account preferences and settings
      • Correct errors in candidate profiles

      Deletion and Restriction:
      • Request deletion of your personal data
      • Restrict processing in certain circumstances
      • Object to processing based on legitimate interests

      Communication Preferences:
      • Opt out of marketing communications
      • Choose notification settings
      • Manage cookie preferences

      To exercise these rights, contact us at privacy@intime.com. We will respond within 30 days and may require identity verification.`
    },
    {
      id: 'cookies',
      title: 'Cookie Policy',
      icon: 'Cookie',
      content: `We use cookies and similar technologies to enhance your experience:

      Essential Cookies:
      • Required for basic platform functionality
      • Authentication and security features  
      • Session management and preferences
      • Cannot be disabled without affecting service

      Analytics Cookies:
      • Track usage patterns and performance
      • Help us understand user behavior
      • Improve our services and features
      • Used for statistical analysis only

      Preference Cookies:
      • Remember your settings and choices
      • Personalize your experience
      • Maintain language and region preferences
      • Enable social media integration

      Third-Party Cookies:
      • Payment processing and fraud prevention
      • Customer support and live chat
      • Marketing and advertising (with consent)
      • Integration with external services

      You can manage cookie preferences through your browser settings or our cookie consent tool.`
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: 'Globe',
      content: `As a global service, we may transfer your data internationally:

      Transfer Safeguards:
      • Standard Contractual Clauses approved by regulators
      • Adequacy decisions for certain jurisdictions
      • Binding Corporate Rules for internal transfers
      • Additional safeguards as required by law

      Data Locations:
      • Primary data centers in [Primary Region]
      • Backup and disaster recovery sites
      • Service provider locations as necessary
      • Regional data residency options available

      Legal Basis:
      • Necessary for contract performance
      • Based on your explicit consent
      • Required for legitimate business interests
      • Compliant with applicable transfer restrictions

      We ensure all international transfers comply with applicable data protection laws, including GDPR Article 44-49 requirements.`
    },
    {
      id: 'policy-changes',
      title: 'Changes to This Policy',
      icon: 'RefreshCw',
      content: `We may update this Privacy Policy from time to time:

      Notification Process:
      • Email notification to registered users
      • Prominent notice on our website
      • In-app notifications for significant changes
      • 30-day advance notice for material changes

      Types of Changes:
      • Updates to reflect new features or services
      • Changes in legal requirements
      • Clarifications or improvements to existing terms
      • Enhanced privacy protections

      Your Options:
      • Continue using our services (acceptance of changes)
      • Contact us with questions or concerns
      • Exercise your data rights before changes take effect
      • Terminate your account if you disagree with changes

      We encourage you to review this policy periodically to stay informed about our privacy practices.`
    }
  ];

  return (
    <div className="space-y-12">
      {/* Privacy Policy Introduction */}
      <section className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <div className="flex items-start">
          <Icon name="Shield" size={24} className="text-blue-600 mr-4 mt-1 flex-shrink-0" />
          <div>
            <h2 className="text-2xl font-bold text-blue-900 mb-3">
              Privacy Policy Overview
            </h2>
            <p className="text-blue-800 text-lg leading-relaxed">
              {highlightSearchTerm(
                "At Intime, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you use our applicant tracking system, and outlines your rights and choices regarding your personal information.",
                searchQuery
              )}
            </p>
          </div>
        </div>
      </section>

      {/* GDPR & CCPA Compliance Notice */}
      <section className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
        <div className="flex items-start">
          <Icon name="CheckCircle" size={20} className="text-green-600 mr-3 mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              Regulatory Compliance
            </h3>
            <p className="text-green-800 text-base">
              {highlightSearchTerm(
                "This Privacy Policy is designed to comply with the EU General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and other applicable privacy laws. We respect your privacy rights and provide mechanisms to exercise them.",
                searchQuery
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Content Sections */}
      {privacyContent?.map((section) => (
        <section key={section?.id} id={section?.id} className="scroll-mt-24">
          <div className="flex items-start mb-6">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <Icon name={section?.icon} size={24} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {highlightSearchTerm(section?.title, searchQuery)}
              </h2>
              <div className="prose prose-lg text-gray-700 leading-relaxed whitespace-pre-line">
                {highlightSearchTerm(section?.content, searchQuery)}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Data Subject Rights Summary */}
      <section className="bg-gray-50 border border-gray-200 rounded-lg p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <Icon name="Scale" size={20} className="mr-3 text-blue-600" />
          Quick Reference: Your Privacy Rights
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start">
              <Icon name="Eye" size={16} className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900">Right to Access</h4>
                <p className="text-sm text-gray-600">View and download your personal data</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Icon name="Edit" size={16} className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900">Right to Rectification</h4>
                <p className="text-sm text-gray-600">Correct inaccurate or incomplete data</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Icon name="Trash2" size={16} className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900">Right to Erasure</h4>
                <p className="text-sm text-gray-600">Request deletion of your personal data</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <Icon name="Download" size={16} className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900">Data Portability</h4>
                <p className="text-sm text-gray-600">Transfer your data to another service</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Icon name="StopCircle" size={16} className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900">Right to Restrict</h4>
                <p className="text-sm text-gray-600">Limit how we process your data</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <Icon name="X" size={16} className="mr-3 mt-1 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900">Right to Object</h4>
                <p className="text-sm text-gray-600">Object to certain processing activities</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <p className="text-gray-600 mb-4">
            To exercise any of these rights, please contact our privacy team.
          </p>
          <a
            href="mailto:privacy@intime.com?subject=Privacy Rights Request"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Icon name="Mail" size={16} className="mr-2" />
            Contact Privacy Team
          </a>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyContent;